import type { RefObject } from "react";
import { WaitlistForm } from "@/components/WaitlistForm";
import { type Audience, HERO } from "@/content";
import { AudienceToggle } from "./AudienceToggle";
import { ProductShot } from "./ProductShot";

type HeroProps = {
  audience: Audience;
  onAudienceChange: (audience: Audience) => void;
  /** Attached to the hero switch so the nav knows when it scrolls away. */
  switchRef: RefObject<HTMLDivElement | null>;
};

/**
 * Above-the-fold. The audience switch sits at the top; the headline and
 * subtitle adapt to whoever the visitor is (printer vs. broker). Then the real
 * product screenshot full-width below. The accent glow sits behind via
 * `.accent-glow`.
 */
export function Hero({ audience, onAudienceChange, switchRef }: HeroProps) {
  const hero = HERO[audience];
  return (
    <section id="top" className="relative overflow-hidden">
      <div className="accent-glow pointer-events-none absolute inset-0 -z-10" />

      <div className="mx-auto max-w-3xl px-4 pt-14 text-center sm:px-6 lg:pt-20">
        <div ref={switchRef} className="flex justify-center">
          <AudienceToggle value={audience} onChange={onAudienceChange} />
        </div>

        <h1 className="mt-8 text-balance text-4xl font-extrabold leading-[1.05] tracking-tight text-[var(--foreground)] sm:text-5xl lg:text-6xl">
          {hero.titleLead}{" "}
          <span className="text-[var(--accent)] transition-colors duration-300">
            {hero.titleAccent}
          </span>
        </h1>

        <p className="mx-auto mt-5 max-w-xl text-pretty text-lg leading-relaxed text-[var(--muted)]">
          {hero.sub}
        </p>

        <div className="mx-auto mt-8 w-full max-w-md">
          <WaitlistForm source="hero" audience={audience} size="lg" />
          <p className="mt-3 text-sm text-[var(--muted)]">
            Sin tarjeta. Te avisamos cuando haya fecha de lanzamiento.
          </p>
        </div>
      </div>

      <div className="mx-auto mt-14 max-w-6xl px-4 sm:px-6 lg:mt-16">
        <ProductShot />
      </div>
    </section>
  );
}
